import * as moment from 'moment';
import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IPendingSwap } from '@nkr/models/index';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

export interface IOpoSwaps {
    donorSwaps: IPendingSwap[],
    remoteDonors: IPendingSwap[]
}

interface IRetVal {
    swaps: IOpoSwaps,
    loading: boolean,
    error: string
}

const emptySwaps: IOpoSwaps = { donorSwaps: [], remoteDonors: [] };

const useOpoSwaps = (isPast: boolean, timeframe: number): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [swaps, setSwaps] = useState<IOpoSwaps>(emptySwaps);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const url = isPast
            ? `${__API__}/opo/${opoId}/past-transplants/${moment().add(timeframe * -1, 'months').format('YYYY-MM-DD')}`
            : `${__API__}/opo/${opoId}/pending-swaps`;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync(isPast ? 'Getting past swaps, one moment please...' : 'Getting pending swaps, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoSwaps> = await dispatch(AuthActionCreators.apiGet({ url }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setSwaps(response.data.results);
                } else {
                    setSwaps(emptySwaps);
                    setError('Unable to load swaps');
                }
            } catch (err) {
                if (isCurrent) {
                    setSwaps(emptySwaps);
                    setError('Unable to load swaps');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, isPast, timeframe]);

    return { swaps, loading, error };
}

export default useOpoSwaps;
